import React from 'react';
import PropTypes from 'prop-types';
import { BillingSummary, PackageCard, AssociationNotice } from './PortalCards';
import { fullName, money, polishDate, lessonTypeLabel } from './clientPortalSelectors';

export default function ClientPortalSection({ section, data, snapshot, selectedStudentId, renderLesson }) {
  const ledgerLabels = {
    PAYMENT: 'Wpłata', OPENING_BALANCE: 'Saldo początkowe', MONTHLY_CHARGE: 'Naliczenie miesięczne', CORRECTION: 'Korekta', REFUND: 'Zwrot',
  };
  const fallback = (snapshot.students || []).length === 1 ? data.unassociated : { packages: [], courses: [], materials: [] };
  const lessonTypes = snapshot.lessonTypes || [];
  if (section === 'schedule') return <section className="client-portal__card">{data.schedule.length ? data.schedule.map(renderLesson) : <p>Brak nadchodzących lekcji</p>}</section>;
  if (section === 'packages') {
    return (
      <section className="client-portal__card">
        {data.unknownAssociations.packages && <AssociationNotice />}
        {data.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        {fallback.packages.length > 0 && (
        <div><h2>Dane bez przypisania do ucznia</h2>
          {fallback.packages.map(item => <PackageCard key={item.id} item={item} lessonTypes={lessonTypes} />)}
        </div>
        )}
        {!data.packages.length && !data.unknownAssociations.packages && <p>Brak pakietów</p>}
      </section>
    );
  }
  if (section === 'billing') {
    return (
      <>
        <BillingSummary ledger={data.ledger} client={snapshot.client || {}} />
        {data.ledger && (
        <section className="client-portal__card"><h2>Operacje płatnika</h2>
          <p>Wpłaty: {money(data.ledger.paymentsTotal)}</p><p>Naliczenia: {money(data.ledger.chargesTotal)}</p>
          {!(data.ledger.entries || []).length && <p>Brak operacji</p>}
          {(data.ledger.entries || []).map(entry => (
            <article className="client-portal__detail-row" key={entry.id}>
              <div>{polishDate(entry.date)}</div><div>{ledgerLabels[entry.type] || 'Operacja rozliczeniowa'}</div>
              {entry.description && <div>{entry.description}</div>}<strong>{money(entry.amount)}</strong>
            </article>
          ))}
        </section>
        )}
        <section className="client-portal__card"><h2>Faktury</h2>{!data.invoices.length && <p>Brak faktur</p>}
          {data.invoices.map(invoice => (
            <article className="client-portal__detail-row" key={invoice.id}><div>{invoice.invoice || invoice.id}</div>
              {/^https?:\/\//i.test(invoice.invoiceUrl || '') && <a href={invoice.invoiceUrl} target="_blank" rel="noopener noreferrer">Otwórz fakturę</a>}
              {invoice.date && <div>{polishDate(invoice.date)}</div>}{invoice.amount != null && <div>{money(invoice.amount)}</div>}
            </article>
          ))}
        </section>
      </>
    );
  }
  if (section === 'summaries') {
    const students = (snapshot.students || []).filter(student => !selectedStudentId || student.id === selectedStudentId);
    return (
      <section className="client-portal__card client-portal__lesson-summaries">
        {!students.length && <p>Brak przypisanych uczniów</p>}
        {students.map(student => (
          <div key={student.id}><h2>{fullName(student)}</h2>
            {!data.summaries.some(item => item.studentId === student.id) && <p>Brak zakończonych zajęć</p>}
            {data.summaries.filter(item => item.studentId === student.id).map(item => (
              <article key={`${item.lessonId}-${item.studentId}`} className="client-portal__lesson-summary">
                <p className="text-muted">{polishDate(item.date)}{item.startTime ? `, ${item.startTime}` : ''}</p>
                {item.status === 'cancelled' && <p>Lekcja odwołana</p>}
                {item.status === 'noSummary' && <p>Nie dodano podsumowania.</p>}
                {item.status === 'summarized' && <p>{item.lessonSummary}</p>}
              </article>
            ))}
          </div>
        ))}
      </section>
    );
  }
  if (section === 'attendance') {
    return (
      <section className="client-portal__card">
        {data.attendance ? <p>Obecności: {data.attendance.present}/{data.attendance.total} ({data.attendance.rate}%)</p> : <p>Brak danych o frekwencji</p>}
        {data.attendanceLessons.map(item => (
          <article className="client-portal__detail-row" key={`${item.id}-${item.studentId}`}>
            <div>{polishDate(item.date)} {item.startTime}</div><div>{item.studentName}</div><div>{item.isPresent ? 'Obecność' : 'Nieobecność'}</div>
          </article>
        ))}
      </section>
    );
  }
  if (section === 'history') {
    return (
      <>
        <section className="client-portal__card"><h2>Lekcje</h2>{!data.historyLessons.length && <p>Brak historii lekcji</p>}
          {data.historyLessons.map(item => (
            <article key={`${item.id}-${item.studentId}`} className="client-portal__detail-row">
              <div>{polishDate(item.date)} {item.startTime}</div><div>{item.studentName}</div><div>{lessonTypeLabel(item.classTypeId, lessonTypes)}</div>
            </article>
          ))}
        </section>
        <section className="client-portal__card"><h2>Kursy</h2>{data.unknownAssociations.courses && <AssociationNotice />}
          {!data.courses.length && !data.unknownAssociations.courses && <p>Brak historii kursów</p>}
          {fallback.courses.length > 0 && (
          <div><h2>Dane bez przypisania do ucznia</h2>
            {fallback.courses.map(item => (
              <article className="client-portal__detail-row" key={item.id}>
                <div>{lessonTypeLabel(item.classTypeId, lessonTypes)}</div>
                {item.fromDate && <div>Od: {polishDate(item.fromDate)}</div>}{item.tillDate && <div>Do: {polishDate(item.tillDate)}</div>}
              </article>
            ))}
          </div>
          )}
          {data.courses.map(item => (
            <article className="client-portal__detail-row" key={item.id}><div>{item.name || lessonTypeLabel(item.classTypeId, lessonTypes)}</div>
              {item.fromDate && <div>Od: {polishDate(item.fromDate)}</div>}{item.tillDate && <div>Do: {polishDate(item.tillDate)}</div>}
            </article>
          ))}
        </section>
      </>
    );
  }
  return (
    <section className="client-portal__card">
      {data.unknownAssociations.materials && <AssociationNotice />}
      {!data.materials.length && !data.unknownAssociations.materials && <p>Brak materiałów</p>}
      {fallback.materials.length > 0 && (
      <div><h2>Dane bez przypisania do ucznia</h2>
        {fallback.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h3>{item.title}</h3>{item.description && <p>{item.description}</p>}</article>)}
      </div>
      )}
      {data.materials.map(item => <article className="client-portal__detail-row" key={item.id}><h2>{item.title}</h2>{item.description && <p>{item.description}</p>}</article>)}
    </section>
  );
}
ClientPortalSection.propTypes = {
  section: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  snapshot: PropTypes.object.isRequired,
  selectedStudentId: PropTypes.string,
  renderLesson: PropTypes.func.isRequired,
};
ClientPortalSection.defaultProps = { selectedStudentId: null };
